/**
 * Shared device-local release gate.
 *
 * GO events start at local wall-clock time in every player's own timezone
 * (docs/event-time-zones.md), so scheduled content carries a NAIVE local
 * wall-clock string ("2026-09-05T10:00", no zone suffix) and the gate resolves
 * it on the DEVICE clock — `new Date("…T10:00")` parses in the device zone,
 * which is exactly the event semantics.
 *
 * First shipped for the Mega roster overlay (megaRosterOverrides.ts, the GO
 * Fest 2026 Mega Finale Kalos starters); lifted here 2026-08-27 so event
 * COSTUMES (FormVariant.releaseAtLocal) share one implementation instead of
 * two drifting copies.
 */

/**
 * How far ahead of its GO release scheduled content shows up in Nexus Dex
 * (2026-09-15 owner direction: trainers use the app to look AHEAD to events,
 * so a costume or Mega dated for next week is drawn, tickable and radar-
 * listed now, not on the morning it lands). Two weeks covers the window
 * LeekDuck usually announces in; anything dated further out stays hidden so
 * a far-future placeholder cannot leak.
 */
export const RELEASE_LOOKAHEAD_DAYS = 14;
const RELEASE_LOOKAHEAD_MS = RELEASE_LOOKAHEAD_DAYS * 24 * 60 * 60 * 1000;

/**
 * Is this local release moment within the look-ahead window on the given
 * clock (or already past)? A malformed or absent date must never hide
 * content — both count as released.
 */
export function isReleasedAtLocal(
  releaseAtLocal: string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!releaseAtLocal) return true;
  const release = new Date(releaseAtLocal);
  if (Number.isNaN(release.getTime())) return true;
  return now.getTime() + RELEASE_LOOKAHEAD_MS >= release.getTime();
}

/** Has the release moment itself passed (no look-ahead)? For "from 16 Sep" style captions. */
export function isReleaseMomentPast(
  releaseAtLocal: string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!releaseAtLocal) return true;
  const release = new Date(releaseAtLocal);
  if (Number.isNaN(release.getTime())) return true;
  return now.getTime() >= release.getTime();
}
